import { md5 } from 'js-md5';

const CACHE_PREFIX = 'hyxino:cms:';

export type CmsCacheEntry<T> = {
  hash: string;
  data: T;
  updatedAt: number;
};

function stableStringify(value: unknown): string {
  const seen = new WeakSet<object>();

  const normalize = (input: unknown): unknown => {
    if (input === undefined) {
      return null;
    }
    if (input === null || typeof input !== 'object') {
      return input;
    }
    if (seen.has(input as object)) {
      return null;
    }
    seen.add(input as object);

    if (Array.isArray(input)) {
      return input.map(normalize);
    }

    const obj = input as Record<string, unknown>;
    const keys = Object.keys(obj).sort();
    const out: Record<string, unknown> = {};
    for (const key of keys) {
      out[key] = normalize(obj[key]);
    }
    return out;
  };

  return JSON.stringify(normalize(value));
}

export function hashCmsData(data: unknown): string {
  return md5(stableStringify(data));
}

export function readCmsCache<T>(key: string): T | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = localStorage.getItem(`${CACHE_PREFIX}${key}`);
    if (!raw) {
      return null;
    }
    const entry = JSON.parse(raw) as CmsCacheEntry<T>;
    return entry?.data ?? null;
  } catch {
    return null;
  }
}

export function writeCmsCache<T>(key: string, data: T): string {
  const hash = hashCmsData(data);
  if (typeof window !== 'undefined') {
    const entry: CmsCacheEntry<T> = {
      hash,
      data,
      updatedAt: Date.now()
    };
    try {
      localStorage.setItem(`${CACHE_PREFIX}${key}`, JSON.stringify(entry));
    } catch {
      /* 配额满或隐私模式时忽略 */
    }
  }
  return hash;
}

export function readCmsCacheHash(key: string): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = localStorage.getItem(`${CACHE_PREFIX}${key}`);
    if (!raw) {
      return null;
    }
    const entry = JSON.parse(raw) as CmsCacheEntry<unknown>;
    return entry?.hash ?? null;
  } catch {
    return null;
  }
}

export function cmsCacheKey(...parts: string[]): string {
  return parts.join(':');
}
